import { WebSocketParams } from '@shared/models/interfaces/websockets'
import { Nullable } from '@shared/models/types/primary'
import { isProd } from '@shared/utils/constants/app-mode'
import { lsGet, LsGlobalKeys } from '@shared/utils/helpers/local-storage'

import { Landing } from '@protots/apps/pc/response/leads/landing'
import { PeerProps } from '@protots/auth/peer_props'
import { Session } from '@protots/auth/session'
import { SessionData } from '@protots/auth/session_data'

import { getAuthSession } from '@pc/helpers/session'
import { PatientSessionRO } from '@pc/models/proto/ro/leads'
import { JsonValue } from '@protobuf-ts/runtime'

const userSessionDataProvider = (sessionData: SessionData) => {
  const session = Session.fromJson(
    (sessionData?.authSession || {}) as JsonValue
  )

  const peerProps = PeerProps.fromJson(
    (sessionData?.peerProps || {}) as JsonValue
  )

  return { session, peerProps }
}

const patientSessionDataProvider = (landingData: Landing) => {
  const landing = landingData
    ? PatientSessionRO.fromJson(landingData as JsonValue)
    : null

  const session = Session.create(
    getAuthSession(landing?.data?.landingState) ?? {}
  )

  const peerProps = PeerProps.create(landing?.data?.peerProps || {})

  return { session, peerProps }
}

const getProdSessionData = () => {
  const isPatient = Boolean(window.landing)

  return isPatient
    ? patientSessionDataProvider(window.landing)
    : userSessionDataProvider(window.sessionData)
}

export const SessionProvider = {
  apiEssentials() {
    const { wsToken } = this.getStoredSession() || {}
    const { wsUrl } = this.getPeerProps() || {}
    const wsParams: Nullable<WebSocketParams> =
      wsToken && wsUrl ? { wsToken, wsUrl } : null

    return {
      wsParams,
    }
  },
  getStoredSession() {
    const prodSession = getProdSessionData().session
    const existingSession = lsGet(LsGlobalKeys.Crd)
    const devSession = existingSession
      ? Session.fromJsonString(existingSession)
      : null

    const session = isProd ? prodSession : devSession

    return session
  },
  getPeerProps() {
    const prodPeerProps = getProdSessionData().peerProps

    const existingPeerProps = lsGet(LsGlobalKeys.PeerProps)
    const devPeerProps = existingPeerProps
      ? PeerProps.fromJsonString(existingPeerProps)
      : null

    const peerProps = isProd ? prodPeerProps : devPeerProps

    return peerProps
  },
  baseUrl() {
    return (
      this.getPeerProps()?.apiHost ||
      (process.env.VUE_APP_API_ROOT_URL as string)
    )
  },
  getCurrentUser() {
    const { user, impersonatedAs } = this.getStoredSession()!

    return impersonatedAs ?? user
  },
  chatUserId() {
    return this.getCurrentUser()?.chatUserId
  },
}
